import {
  DynamicWidgets,
  useClearRefinements,
  useHierarchicalMenu,
  useRefinementList,
  useToggleRefinement,
} from 'react-instantsearch';
import Chevron from '@/assets/icons/ChevronDown.svg';
import { Checkbox } from '@/components/Checkbox';
import { useSearchData } from '@/contexts/searchContext';
import { useDataLayer } from '@/hooks/useDataLayer';
import * as Styled from './FilterPanel.styled';

interface FilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

// Which facets appear and in what order is driven by the Algolia index's
// `renderingContent.facetOrdering` (configured in the dashboard), consumed by
// <DynamicWidgets> below — not by a hardcoded list. facetOrdering carries
// attribute names but no display labels, so map attribute -> label here;
// unknown attributes fall back to a prettified attribute name.
const FACET_LABELS: Record<string, string> = {
  author: 'Author',
  format: 'Format',
  publicationDateRange: 'Publication Date',
  productType: 'Product Type',
  bookType: 'Book Type',
  brand: 'Brand',
  ageRange: 'Age Range',
  priceRange: 'Price Range',
  colour: 'Colour',
  packSize: 'Pack Size',
  nibSize: 'Nib Size',
  seriesCode: 'Series',
};

const prettifyAttribute = (attribute: string): string =>
  attribute
    .replace(/\./g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, char => char.toUpperCase());

const labelFor = (attribute: string): string =>
  FACET_LABELS[attribute] ?? prettifyAttribute(attribute);

const filterVisibleFacets = (attributes: string[]): string[] =>
  attributes.filter(attribute => attribute !== 'seriesCode');

interface HierarchicalMenuItem {
  count: number;
  data: HierarchicalMenuItem[] | null;
  isRefined: boolean;
  label: string;
  value: string;
}

const CategoryItems = ({
  items,
  refine,
}: {
  items: HierarchicalMenuItem[];
  refine: (value: string) => void;
}) => (
  <ul>
    {items.map(item => (
      <li key={item.value}>
        <Styled.CategoryLink
          $isRefined={item.isRefined}
          href="#"
          onClick={e => {
            e.preventDefault();
            refine(item.value);
          }}
        >
          {item.label}
        </Styled.CategoryLink>
        {item.data && item.data.length > 0 ? (
          <CategoryItems items={item.data} refine={refine} />
        ) : null}
      </li>
    ))}
  </ul>
);

const CategoryFacet = () => {
  const { filterApply } = useDataLayer();
  const { items, refine } = useHierarchicalMenu({
    attributes: [
      'hierarchicalCategories.lvl0',
      'hierarchicalCategories.lvl1',
      'hierarchicalCategories.lvl2',
      'hierarchicalCategories.lvl3',
    ],
    limit: 20,
    showParentLevel: true,
  });

  if (items.length === 0) {
    return null;
  }

  const trackedRefine = (value: string) => {
    refine(value);
    filterApply('category', value);
  };

  return (
    <Styled.FacetSection>
      <h2>Category</h2>
      <CategoryItems
        items={items as HierarchicalMenuItem[]}
        refine={trackedRefine}
      />
    </Styled.FacetSection>
  );
};

// Generic refinement-list facet. Used both as explicit <DynamicWidgets>
// children (where special props are needed) and as the fallbackComponent for
// every other facet Algolia's facetOrdering returns. The fallback path is
// called with only `{ attribute }`, so all other props are optional.
const RefinementFacet = ({
  attribute,
  limit = 4,
  showMoreLimit = 50,
  transformLabel,
}: {
  attribute: string;
  limit?: number;
  showMoreLimit?: number;
  transformLabel?: (value: string) => string;
}) => {
  const { filterApply } = useDataLayer();
  const { items, refine, canToggleShowMore, isShowingMore, toggleShowMore } =
    useRefinementList({
      attribute,
      limit,
      showMore: true,
      showMoreLimit,
    });

  if (items.length <= 1) {
    return null;
  }

  return (
    <Styled.FacetSection>
      <h2>{labelFor(attribute)}</h2>
      <ul>
        {items.map(item => (
          <li key={item.value}>
            <Checkbox
              isSelected={item.isRefined}
              label={
                <>
                  <span>{transformLabel ? transformLabel(item.label) : item.label}</span>
                  <Styled.FacetCount>({item.count.toLocaleString()})</Styled.FacetCount>
                </>
              }
              onChange={() => {
                refine(item.value);
                filterApply(attribute, item.value);
              }}
            />
          </li>
        ))}
      </ul>
      {canToggleShowMore ? (
        <Styled.ShowMoreButton
          $isExpanded={isShowingMore}
          onClick={toggleShowMore}
          type="button"
        >
          {isShowingMore ? 'Show less' : 'Show more'}
          <Chevron />
        </Styled.ShowMoreButton>
      ) : null}
    </Styled.FacetSection>
  );
};

export const FilterPanel = ({ isOpen, onClose }: FilterPanelProps) => {
  const { ageRanges } = useSearchData();
  const { filterApply } = useDataLayer();
  const { value: inStockValue, refine: toggleInStock } = useToggleRefinement({
    attribute: 'inStock',
  });
  const { refine: clearAll, canRefine } = useClearRefinements();

  const mapAgeLabel = (code: string): string => {
    const range = ageRanges.find(r => r.code === code);
    return range?.label ?? code;
  };

  return (
    <>
      <Styled.Overlay $isOpen={isOpen} onClick={onClose} />
      <Styled.Panel $isOpen={isOpen}>
        <Styled.CloseButton onClick={onClose} type="button">
          ✕
        </Styled.CloseButton>

        <Styled.InStockSection>
          <Checkbox
            isSelected={inStockValue.isRefined}
            label="Show in stock only"
            onChange={() => {
              toggleInStock(inStockValue);
              // isRefined is the pre-toggle state, so the target is its inverse.
              filterApply('inStock', inStockValue.isRefined ? 'off' : 'on');
            }}
          />
        </Styled.InStockSection>

        {/* Category is the primary hierarchical nav — always rendered first,
            kept outside DynamicWidgets so it is unaffected by facetOrdering. */}
        <CategoryFacet />

        {/* Flat facets: which ones show and in what order comes from Algolia's
            renderingContent.facetOrdering. Generic facets render through the
            fallbackComponent; ageRange (label transform) and priceRange (wider
            limit) are explicit children so they keep their special handling
            while still ordering dynamically. Requires renderingContent to be
            configured on the index — without it DynamicWidgets renders nothing. */}
        <DynamicWidgets
          fallbackComponent={RefinementFacet}
          maxValuesPerFacet={100}
          transformItems={filterVisibleFacets}
        >
          <RefinementFacet
            attribute="ageRange"
            showMoreLimit={100}
            transformLabel={mapAgeLabel}
          />
          <RefinementFacet attribute="priceRange" limit={10} />
        </DynamicWidgets>

        {/* These facets are never shown but kept mounted (hidden) so URL-driven
            refinements still register and filter results. DynamicWidgets only
            mounts facets in facetOrdering, so these stay outside it. */}
        <div style={{ display: 'none' }}>
          <RefinementFacet attribute="seriesCode" />
          <RefinementFacet attribute="isIrishInterest" />
        </div>

        <Styled.MobileButtons>
          {canRefine ? (
            <Styled.ApplyButton
              onClick={() => {
                clearAll();
                onClose();
              }}
              type="button"
            >
              Clear all
            </Styled.ApplyButton>
          ) : null}
          <Styled.ApplyButton onClick={onClose} type="button">
            Apply Filters
          </Styled.ApplyButton>
        </Styled.MobileButtons>
      </Styled.Panel>
    </>
  );
};
